import { useEffect, useRef, useState } from 'react';

import { usePolling } from '@/hooks/usePolling';
import type { ChatProgressData } from '@/lib/reasoning/realtime/chatProgressPoll';
import { classifyChatProgress } from '@/lib/reasoning/realtime/chatProgressPoll';

import type {
  PartialStepsPayload,
  ReasoningStepSummary,
} from './latestStepSummary';
import {
  extractLatestStepSummary,
  isSameStepSummary,
} from './latestStepSummary';

interface UseProposalPollingOptions {
  onComplete: () => void;
  onError: (error: Error) => void;
}

interface ProgressData extends ChatProgressData, PartialStepsPayload {
  // Celery-progress fields. The backend writes status text like
  // "Starting reasoning process…" / "Processing step 3…" / "✓ Updated…"
  // every time progress advances. The shape is sometimes flat
  // (`description`) and sometimes nested (`progress.description`) — we
  // accept both because both are produced by `celery-progress` in the
  // wild and the codebase reads both in ReasoningChat.tsx.
  description?: unknown;
  progress?: { description?: unknown };
}

function extractProgressDescription(data: ProgressData): string | undefined {
  const flat = data.description;
  if (typeof flat === 'string' && flat.trim().length > 0) {
    return flat;
  }
  const nested = data.progress?.description;
  if (typeof nested === 'string' && nested.trim().length > 0) {
    return nested;
  }
  return undefined;
}

/**
 * Specialized polling hook for proposal discussion conversations.
 * Wraps the base usePolling hook with proposal-specific completion logic.
 *
 * Also tracks the most recent completed reasoning step so the chat UI can
 * surface what the agent is working on while the user waits.
 *
 * @param taskId - The task ID from the discuss API response
 * @param conversationId - The conversation ID as string
 * @param orgId - Optional organization ID
 * @param options - Callbacks for completion and error handling
 */
export function useProposalPolling(
  taskId: string | null,
  conversationId: string | null,
  orgId: string | undefined,
  options: UseProposalPollingOptions
) {
  const { onComplete, onError } = options;
  const [latestStep, setLatestStep] = useState<ReasoningStepSummary | null>(
    null
  );
  // Celery-progress description — updated every poll well before the first
  // step row commits, so it's the right thing to show in the popover for
  // the first ~few seconds of every turn.
  const [progressDescription, setProgressDescription] = useState<string | null>(
    null
  );

  // Reset the cached state ONLY when a new task starts — not when the
  // current task ends. After completion we want `latestStep` to persist
  // so the AgentResponsePanel can display whatever the agent produced.
  // The progress description, by contrast, is a transient running-task
  // status and should clear immediately.
  const previousTaskIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (taskId && taskId !== previousTaskIdRef.current) {
      setLatestStep(null);
      setProgressDescription(null);
    }
    previousTaskIdRef.current = taskId;
  }, [taskId]);

  const { isPolling } = usePolling(taskId, conversationId, orgId, {
    enabled: !!taskId && !!conversationId,
    interval: 1000,
    onUpdate: data => {
      const progress = data as ProgressData;
      const next = extractLatestStepSummary(progress);
      if (next) {
        // Skip the state update if nothing changed — prevents a re-render
        // every poll tick when the agent hasn't completed a new step yet.
        setLatestStep(prev => (isSameStepSummary(prev, next) ? prev : next));
      }

      const nextDescription = extractProgressDescription(progress);
      if (nextDescription !== undefined) {
        setProgressDescription(prev =>
          prev === nextDescription ? prev : nextDescription
        );
      }

      // Distinguish success from failure so the UI can show an error toast
      // for failed chains instead of silently swallowing the failure. A
      // proposal turn surfaces as `paused` when it is done and waiting for
      // review, so paused counts as complete here.
      const outcome = classifyChatProgress(progress, {
        treatPausedAsComplete: true,
      });

      if (outcome !== 'in_progress') {
        // Keep `latestStep` so the AgentResponsePanel can show what the
        // agent produced. It's reset above when a new task starts.
        setProgressDescription(null);
        if (outcome === 'failed') {
          onError(new Error('Something went wrong processing your message.'));
        } else {
          onComplete();
        }
      }
    },
    onError: errorMessage => {
      onError(new Error(errorMessage));
    },
  });

  return { isPolling, latestStep, progressDescription };
}
